/**
 * Standardised dataLayer events for GTM (GTM-WZP9BZHB).
 *
 * Architecture:
 *   React app -> dataLayer.push({ event: "crs_*", ... }) -> GTM -> GA4 / Google Ads.
 *
 * Rules enforced here (application layer):
 *   1. No PII in event payloads (no name/email/phone/address/postcode/message).
 *   2. Dedup via module Set + sessionStorage keyed on `${event}:${lead_id}`.
 *   3. Events push regardless of consent - Consent Mode / GTM decide onward transmission.
 *   4. SSR-safe no-ops; bot / Lovable-preview no-op via window.__crsAnalyticsDisabled.
 */

import { hasAnalyticsConsent } from "@/lib/cookie-consent";
import { phCapture } from "@/lib/posthog";

// ---------- dataLayer plumbing ----------

type DataLayerRecord = Record<string, unknown>;

function pushDataLayer(record: DataLayerRecord) {
  if (typeof window === "undefined") return;
  if ((window as unknown as { __crsAnalyticsDisabled?: boolean }).__crsAnalyticsDisabled) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(record);
}

// ---------- Layer 1 dedup ----------

const SENT_KEY = "crs_sent_events";
const sentInMemory = new Set<string>();

function loadPersistedSent(): Set<string> {
  if (typeof window === "undefined") return sentInMemory;
  try {
    const raw = window.sessionStorage.getItem(SENT_KEY);
    if (!raw) return sentInMemory;
    for (const id of JSON.parse(raw) as string[]) sentInMemory.add(id);
  } catch {
    /* ignore */
  }
  return sentInMemory;
}

function persistSent() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SENT_KEY, JSON.stringify([...sentInMemory]));
  } catch {
    /* ignore */
  }
}

function markAndCheck(dedupKey: string): boolean {
  const store = loadPersistedSent();
  if (store.has(dedupKey)) return false;
  store.add(dedupKey);
  persistSent();
  return true;
}

// ---------- Page-view sanitisation ----------

const PII_PARAM_KEYS = new Set([
  "email",
  "e_mail",
  "phone",
  "tel",
  "name",
  "postcode",
  "postal",
  "token",
  "code",
  "otp",
  "auth",
]);

export function sanitisePagePath(pathname: string, search: string): string {
  try {
    const params = new URLSearchParams(search);
    const toDelete: string[] = [];
    params.forEach((_, key) => {
      const k = key.toLowerCase();
      if (k.startsWith("__lovable_") || PII_PARAM_KEYS.has(k)) toDelete.push(key);
    });
    toDelete.forEach((k) => params.delete(k));
    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  } catch {
    return pathname;
  }
}

// ---------- Approved event helpers ----------

let lastPageViewKey: string | null = null;

export function crsPageView(locationKey?: string) {
  if (typeof window === "undefined") return;
  const key = locationKey ?? `${window.location.pathname}${window.location.search}`;
  if (key === lastPageViewKey) return;
  lastPageViewKey = key;

  const path = sanitisePagePath(window.location.pathname, window.location.search);
  pushDataLayer({
    event: "crs_page_view",
    page_path: path,
    page_location: window.location.origin + path,
    page_title: typeof document !== "undefined" ? document.title : "",
  });
}

export type LeadType =
  | "solar"
  | "heat-pump"
  | "finance"
  | "contact"
  | "callback"
  | "b2b"
  | "grants"
  | "estimate-details"
  | "other";

export function crsLeadSubmitted(opts: {
  leadId: string;
  formId: string;
  leadType: LeadType;
}) {
  if (!opts.leadId) return;
  const dedupKey = `crs_lead_submitted:${opts.leadId}`;
  if (!markAndCheck(dedupKey)) return;
  phCapture("form_submitted", {
    lead_id: opts.leadId,
    form_name: opts.formId,
    form_type: opts.leadType,
  });
  pushDataLayer({
    event: "crs_lead_submitted",
    lead_id: opts.leadId,
    form_id: opts.formId,
    lead_type: opts.leadType,
    page_path: typeof window !== "undefined"
      ? sanitisePagePath(window.location.pathname, window.location.search)
      : "",
  });
}

export function crsAppointmentBooked(opts: { bookingId: string; source: string }) {
  if (!opts.bookingId) return;
  const dedupKey = `crs_appointment_booked:${opts.bookingId}`;
  if (!markAndCheck(dedupKey)) return;
  phCapture("booking_created", { booking_id: opts.bookingId, booking_source: opts.source });
  pushDataLayer({
    event: "crs_appointment_booked",
    booking_id: opts.bookingId,
    booking_source: opts.source,
  });
}

export function crsSolarCalculatorCompleted(opts: {
  leadId: string;
  capturedContact: boolean;
}) {
  if (!opts.leadId) return;
  const dedupKey = `crs_solar_calculator_completed:${opts.leadId}`;
  if (!markAndCheck(dedupKey)) return;
  pushDataLayer({
    event: "crs_solar_calculator_completed",
    lead_id: opts.leadId,
    captured_contact: opts.capturedContact,
  });
}

/**
 * /design-solar funnel milestones. PII-free by construction: only step
 * numbers, intent classification and an opaque lead id are ever sent.
 */
export function crsDesignSolarEvent(
  action:
    | "design_lp_view"
    | "qualification_step"
    | "qualification_completed"
    | "calendar_opened"
    | "design_call_booked",
  payload: {
    step?: number;
    intent?: string;
    lead_id?: string;
    cta_location?: string;
    once?: boolean;
  } = {},
) {
  const { once, ...rest } = payload;
  if (once) {
    const key = `crs_${action}:${rest.lead_id ?? rest.step ?? "page"}`;
    if (!markAndCheck(key)) return;
  }
  pushDataLayer({ event: `crs_${action}`, ...rest });
}

export function crsPhoneClicked(location: string) {
  pushDataLayer({ event: "crs_phone_clicked", phone_location: location });
}

export function crsEmailClicked(location: string) {
  pushDataLayer({ event: "crs_email_clicked", email_location: location });
}

export function crsWhatsappClicked(location: string) {
  pushDataLayer({ event: "crs_whatsapp_clicked", whatsapp_location: location });
}

export function crsFinanceApplicationStarted(location: string) {
  pushDataLayer({ event: "crs_finance_application_started", cta_location: location });
}

export function crsBillUploaded(context: string) {
  pushDataLayer({ event: "crs_bill_uploaded", upload_context: context });
}

export function crsChatCompleted(opts: { chatId: string }) {
  const dedupKey = `crs_chat_completed:${opts.chatId}`;
  if (!markAndCheck(dedupKey)) return;
  pushDataLayer({ event: "crs_chat_completed", chat_id: opts.chatId });
}

// ---------- Delegated click listener (tel/mailto/whatsapp/finance) ----------

function nearestRegion(el: Element | null): string {
  let cur: Element | null = el;
  while (cur) {
    const region = cur.getAttribute?.("data-analytics-region");
    if (region) return region;
    cur = cur.parentElement;
  }
  return "unknown";
}

let listenerInstalled = false;
export function installDelegatedClickTracking() {
  if (typeof window === "undefined" || listenerInstalled) return;
  listenerInstalled = true;
  document.addEventListener(
    "click",
    (ev) => {
      const target = ev.target as Element | null;
      if (!target) return;
      const anchor = (target.closest?.("a[href]") ?? null) as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href") ?? "";
      const region = nearestRegion(anchor);
      if (href.startsWith("tel:")) {
        crsPhoneClicked(region);
      } else if (href.startsWith("mailto:")) {
        crsEmailClicked(region);
      } else if (/(?:wa\.me|api\.whatsapp\.com|web\.whatsapp\.com)/i.test(href)) {
        crsWhatsappClicked(region);
      } else if (/ideal4finance\.com\/loan-apply/i.test(href)) {
        crsFinanceApplicationStarted(region);
      }
    },
    { capture: true },
  );
}

// ---------- Legacy compatibility ----------

/**
 * @deprecated Consent-gated ad-hoc dataLayer push. Prefer the typed helpers above.
 * Kept for existing callers (complete-your-estimate). Do not expand usage.
 */
export function trackAnalyticsEvent(event: string, payload: DataLayerRecord = {}) {
  if (typeof window === "undefined") return;
  if (!hasAnalyticsConsent()) return;
  pushDataLayer({ event, ...payload });
}

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/**
 * Lightweight case-study interaction events (no PII, deduped per lead where
 * an id is available). Used by the solar installation case study template.
 */
export function crsCaseStudyEvent(
  action:
    | "case_study_viewed"
    | "next_property_clicked"
    | "previous_property_clicked"
    | "similar_installation_clicked"
    | "estimate_cta_clicked"
    | "contact_form_started"
    | "phone_cta_clicked"
    | "technical_details_expanded",
  payload: { reference_code?: string; location?: string; once?: boolean } = {},
) {
  const { once, ...rest } = payload;
  if (once) {
    const key = `crs_case_study:${action}:${rest.reference_code ?? "page"}`;
    if (!markAndCheck(key)) return;
  }
  pushDataLayer({ event: `crs_${action}`, ...rest });
}
